import { motion } from "framer-motion";
import { QrCode, Smartphone, MessageSquare, Mail, Check, CheckCheck, Star, Trophy, Zap } from "lucide-react";
import { Reveal, Overline } from "@/components/primitives";

/* ---------- Mockups ---------- */

const QR_PATTERN = [
  1, 1, 1, 0, 1, 0, 1, 1, 1,
  1, 0, 1, 0, 0, 1, 1, 0, 1,
  1, 1, 1, 0, 1, 1, 1, 1, 1,
  0, 0, 0, 1, 0, 0, 0, 0, 0,
  1, 1, 0, 1, 1, 0, 1, 0, 1,
  0, 1, 1, 0, 0, 1, 0, 1, 0,
  1, 1, 1, 0, 1, 0, 1, 1, 1,
  1, 0, 1, 1, 0, 1, 1, 0, 0,
  1, 1, 1, 0, 1, 0, 1, 0, 1,
];

const QrMockup = () => (
  <div className="flex items-center justify-center gap-6">
    {/* printed table card */}
    <div className="rotate-[-4deg] rounded-[10px] border border-black/10 bg-white p-5 shadow-[0_24px_50px_-30px_rgba(0,0,0,0.4)]">
      <p className="mb-3 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-black/50">
        Scan to review us
      </p>
      <div className="grid grid-cols-9 gap-[3px] rounded-[6px] bg-white p-2">
        {QR_PATTERN.map((c, i) => (
          <span
            key={i}
            className={`aspect-square rounded-[1px] ${c ? "bg-black" : "bg-transparent"}`}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-center gap-1">
        <Star size={11} className="fill-black text-black" />
        <span className="text-[11px] font-semibold">Leave a Google review</span>
      </div>
    </div>
    {/* phone screen */}
    <div className="rotate-[3deg] w-[130px] rounded-[14px] border-2 border-black bg-white p-2 shadow-[0_24px_50px_-30px_rgba(0,0,0,0.5)]">
      <div className="rounded-[8px] bg-base p-3">
        <div className="mb-2 flex items-center gap-1.5">
          <div className="h-5 w-5 rounded-full bg-black" />
          <div className="h-1.5 w-14 rounded-full bg-black/15" />
        </div>
        <p className="text-[9px] font-medium leading-tight text-black/70">
          How was your visit?
        </p>
        <div className="mt-2 flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={12} className="fill-black text-black" />
          ))}
        </div>
        <div className="mt-2 h-8 rounded-[5px] border border-black/10 bg-white" />
        <div className="mt-2 rounded-[5px] bg-black py-1.5 text-center text-[8px] font-semibold text-white">
          Post review
        </div>
      </div>
    </div>
  </div>
);

const Bubble = ({ side = "in", children, meta }) => (
  <div className={`flex ${side === "out" ? "justify-end" : "justify-start"}`}>
    <div className="max-w-[80%]">
      <div
        className={`rounded-[10px] px-3 py-2 text-[11px] leading-snug ${
          side === "out" ? "bg-black text-white" : "border border-black/10 bg-white text-black/80"
        }`}
      >
        {children}
      </div>
      {meta && (
        <div className={`mt-1 flex items-center gap-1 text-[9px] text-black/40 ${side === "out" ? "justify-end" : ""}`}>
          {meta}
        </div>
      )}
    </div>
  </div>
);

const RequestsMockup = () => (
  <div className="mx-auto w-full max-w-[340px] space-y-2.5 rounded-[10px] border border-black/10 bg-base p-4">
    <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-black/45">
      <MessageSquare size={12} /> SMS
    </div>
    <Bubble side="out" meta={<><CheckCheck size={11} /> Delivered</>}>
      Hi Maria! Thanks for visiting Bloom Café ☕ Mind sharing a quick review?
    </Bubble>
    <Bubble side="in">Just did — 5 stars! ⭐</Bubble>
    <div className="!mt-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-black/45">
      <Mail size={12} /> Email
    </div>
    <div className="rounded-[8px] border border-black/10 bg-white p-3">
      <div className="mb-2 h-1.5 w-20 rounded-full bg-black/15" />
      <p className="text-[11px] leading-snug text-black/70">
        We'd love your feedback on today's visit.
      </p>
      <div className="mt-2 inline-flex rounded-[6px] bg-black px-2.5 py-1 text-[9px] font-semibold text-white">
        Leave a review
      </div>
    </div>
    <div className="flex items-center justify-between rounded-[8px] bg-black px-3 py-2 text-[10px] font-medium text-white">
      <span className="inline-flex items-center gap-1.5"><Zap size={11} /> Automation running</span>
      <span className="inline-flex items-center gap-1 text-white/70"><Check size={11} /> 3 sent today</span>
    </div>
  </div>
);

const LEADERBOARD = [
  { name: "Sofia R.", count: 48, initials: "SR" },
  { name: "James K.", count: 41, initials: "JK" },
  { name: "Aisha M.", count: 33, initials: "AM" },
];

const LeaderboardMockup = () => (
  <div className="mx-auto w-full max-w-[340px] rounded-[10px] border border-black/10 bg-white p-5 shadow-[0_24px_50px_-34px_rgba(0,0,0,0.35)]">
    <div className="mb-4 flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-sm font-semibold">
        <Trophy size={15} /> Team leaderboard
      </span>
      <span className="text-[10px] font-medium text-black/40">This month</span>
    </div>
    <div className="space-y-2.5">
      {LEADERBOARD.map((p, i) => (
        <div
          key={p.name}
          className={`flex items-center gap-3 rounded-[8px] border p-2.5 ${
            i === 0 ? "border-black bg-black/[0.03]" : "border-black/10"
          }`}
        >
          <span className="w-4 text-center font-mono text-xs font-bold text-black/50">{i + 1}</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[10px] font-bold text-white">
            {p.initials}
          </span>
          <span className="flex-1 text-xs font-medium">{p.name}</span>
          <div className="text-right">
            <div className="text-sm font-black leading-none">{p.count}</div>
            <div className="text-[9px] text-black/40">reviews</div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-black/10">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: "82%" }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="h-full rounded-full bg-black"
      />
    </div>
    <p className="mt-2 text-[10px] font-medium text-black/40">82% toward this month's team goal</p>
  </div>
);

/* ---------- Section ---------- */

const FEATURES = [
  {
    icon: QrCode,
    tag: "QR Codes",
    title: "Scan and leave feedback in seconds",
    body: "Place a QR code on tables, receipts, or counters. Customers scan with their phone and leave a review instantly — no app, no friction.",
    mockup: <QrMockup />,
  },
  {
    icon: Smartphone,
    tag: "Automated Requests",
    title: "Review requests that send themselves",
    body: "Reach customers automatically through modern channels like SMS and email, with intelligent timing and clear delivery states.",
    mockup: <RequestsMockup />,
  },
  {
    icon: Trophy,
    tag: "Team-Based Collection",
    title: "Turn your team into a review engine",
    body: "Motivate your team to generate more reviews consistently with subtle, premium performance tracking and friendly rankings.",
    mockup: <LeaderboardMockup />,
  },
];

export default function ReviewCollection() {
  return (
    <section id="collect" className="relative bg-base py-24 lg:py-32">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>Review collection</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter text-black sm:text-5xl lg:text-6xl">
              Make it easy for customers to leave reviews
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-black/60">
              Every method meets customers where they already are — and turns a great
              experience into public proof, automatically.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 space-y-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.tag} delay={0.05}>
              <div
                className={`grid grid-cols-1 items-center gap-10 rounded-[10px] border border-black/10 bg-white p-8 lg:grid-cols-2 lg:gap-16 lg:p-14 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <div className="inline-flex items-center gap-2 rounded-[10px] border border-black/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-black/60">
                    <f.icon size={14} /> {f.tag}
                  </div>
                  <h3 className="mt-5 text-2xl font-medium tracking-tight text-black sm:text-3xl">
                    {f.title}
                  </h3>
                  <p className="mt-4 max-w-md text-base font-light leading-relaxed text-black/60">
                    {f.body}
                  </p>
                </div>
                <div className="flex items-center justify-center">{f.mockup}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
